import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import RestaurantList from "./components/RestaurantList";
import Filters from "./components/Filters";
import { useFilterContext } from "@/ui/head-bar/filter-context";
import MapView from "./components/MapView";
import MapViewMobileTool from "./components/MapView/tool-mobile";
import MapViewDesktopTool from "./components/MapView/tool-desktop";

import { useLocation } from "react-router-dom";
import { useThrottleFn, useDebounceFn } from "ahooks";
import { observer } from "mobx-react-lite";
import { useStores } from "@/store";
import "./index.less";
import { Footer } from "@/components";
import { Affix } from "antd";
import moment from "moment";
import { MapHomeIcon } from "@/icons";
import cn from "classnames";
import { useTranslation } from "react-i18next";

const useMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { run } = useThrottleFn(
    () => {
      setIsMobile(window.innerWidth <= 768);
    },
    { wait: 500 },
  );

  useEffect(() => {
    window.addEventListener("resize", run);
    return () => {
      window.removeEventListener("resize", run);
    };
  }, [run]);

  return isMobile;
};

const useSmartPosition = () => {
  const { HeaderStore } = useStores();
  const [isPositionReady, setIsPositionReady] = useState(false);

  useEffect(() => {
    const waitForPosition = async () => {
      // 检查是否有缓存的定位信息
      const cachedPos = HeaderStore.pos;
      const lastTimestamp = cachedPos?.time;
      const now = Date.now();

      // 如果有缓存且距离上次定位不到15分钟，直接使用缓存
      if (cachedPos && lastTimestamp && now - lastTimestamp < 15 * 60 * 1000) {
        setIsPositionReady(true);
        return;
      }

      // 触发定位请求
      HeaderStore.getPosition();

      // 等待1500ms
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // 1500ms后，无论是否获取到新定位，都标记为ready
      setIsPositionReady(true);
    };

    waitForPosition();
  }, [HeaderStore]);

  return isPositionReady;
};

const extendDate = (dateStr) => {
  if (!dateStr) return null;
  const date = moment(dateStr);
  if (!date.isValid()) return null;
  return [
    date.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    date.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  ];
};
const paramsBuilder = (params) => {
  const nextParams = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (Array.isArray(value)) {
      if (value.length > 0) nextParams[key] = value;
      return;
    }
    if (value === "" || value == null) return;
    nextParams[key] = value;
  });
  return nextParams;
};

const useResults = ({ isMapFullscreen, isPositionReady }) => {
  const location = useLocation();
  const { BrowseStore } = useStores();
  const { exploreMapMode, searchState, state, pos, mapRange, order_by } =
    useFilterContext();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const nextPageRef = useRef(null); // 由服务端 next_page 驱动，避免本地页码计算偏差
  const [isEmpty, setIsEmpty] = useState(false);
  const isFirstLoadRef = useRef(true);
  const isMobile = useMobile();
  const abortControllerRef = useRef(null);

  // 从 URL 中直接解析 region 参数作为回退值
  const urlSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params;
  }, [location.search]);

  // 获取数据的核心方法
  const fetchData = useCallback(
    (targetPage, isLoadMore = false) => {
      // 等待定位完成后再请求
      if (!isPositionReady) return Promise.resolve();

      // 使用 URL region 作为 region 来源
      if (!urlSearch.get("region")) return Promise.resolve();
      if (isMobile && exploreMapMode) {
        // 移动端地图模式下，不重复请求
        return Promise.resolve();
      }

      // 终止上一个请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // 创建新的 AbortController
      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setResults([]);
      }

      return BrowseStore.getBrowseRestaurants(
        paramsBuilder({
          ...state,
          ...searchState,
          ...pos,
          ...mapRange,
          region: urlSearch.get("region"),
          order_by,
          date: undefined,
          time: undefined,
          datetime: extendDate(urlSearch.get("date")),
          page: targetPage,
          per_page: 50,
        }),
        controller.signal,
      ).then((res) => {
        // 请求被取消时不更新结果
        if (res?.__cancelled__) return;

        const totalCount = Number(
          res?.headers?.["total_count"] || res?.headers?.["x-total-count"] || 0,
        );
        const nextPage = Number(res?.headers?.["next_page"] || 0);

        setTotal(totalCount);
        // 始终以服务端返回的 next_page 为准，避免本地计算偏差
        nextPageRef.current = nextPage || null;
        setIsEmpty(targetPage === 1 && (!res || res.length === 0));

        if (isLoadMore) {
          setResults((prev) => [...prev, ...(res || [])]);
          setLoadingMore(false);
        } else {
          setResults(res || []);
          setTimeout(() => {
            setLoading(false);
          }, 150);
        }
      });
    },
    [
      BrowseStore,
      state,
      searchState,
      pos,
      mapRange,
      urlSearch,
      order_by,
      exploreMapMode,
      isMobile,
      isPositionReady,
    ],
  );

  const { run: debouncedFetch } = useDebounceFn(
    (targetPage, isLoadMore) => {
      // 首次加载时立即执行，后续加载使用防抖
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        return fetchData(targetPage, isLoadMore);
      }
      return fetchData(targetPage, isLoadMore);
    },
    { wait: 300 },
  );

  // 当筛选条件、位置、路由变化时，从第1页重新加载
  useEffect(() => {
    const effectiveRegion = urlSearch.get("region");
    if (!effectiveRegion) return;
    if (isMapFullscreen) return;

    nextPageRef.current = null;
    debouncedFetch(1, false);
    // 滚动到顶部
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state,
    location,
    mapRange,
    order_by,
    isMapFullscreen,
    exploreMapMode,
    isPositionReady,
  ]);

  // 加载更多
  const loadMore = useCallback(() => {
    if (loadingMore || loading) return;
    // nextPageRef 由服务端 next_page 写入，为 null 表示已无更多页
    if (!nextPageRef.current) return;

    const nextPage = nextPageRef.current;
    nextPageRef.current = null; // 请求发出前清空，防止重复触发
    debouncedFetch(nextPage, true);
  }, [loadingMore, loading, debouncedFetch]);

  // 监听滚动到底部
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // 距离底部100px时触发加载
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMore();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMore]);

  return {
    loading,
    loadingMore,
    results,
    total,
    isEmpty,
    loadMore,
  };
};

const useMapResults = ({ mapReqIdRef, isPositionReady }) => {
  const isMobile = useMobile();
  const location = useLocation();
  const { BrowseStore } = useStores();
  const { searchState, state, pos, exploreMapMode, mapRange, searchTrigger } =
    useFilterContext();
  const [results, setResults] = useState([]);
  const isFirstLoadRef = useRef(true);
  const abortControllerRef = useRef(null);

  // 从 URL 中直接解析 region 参数作为回退值
  const urlSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params;
  }, [location.search]);

  // 获取数据的核心方法
  const fetchData = useCallback(() => {
    // 等待定位完成后再请求
    if (!isPositionReady) return Promise.resolve();

    // 使用 URL region 作为 region 来源
    if (!urlSearch.get("region")) return Promise.resolve();

    // 终止上一个请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    return BrowseStore.getBrowseMapRestaurants(
      paramsBuilder({
        ...state,
        ...searchState,
        ...pos,
        ...mapRange,
        region: urlSearch.get("region"),
        date: undefined,
        time: undefined,
        datetime: extendDate(urlSearch.get("date")),
        page: 1,
        per_page: 2000,
      }),
      controller.signal,
    )
      .then((res) => {
        // 请求被取消时不更新结果
        if (res?.__cancelled__) return;
        mapReqIdRef.current += 1;
        setResults(res || []);
      })
      .catch(() => {
        mapReqIdRef.current += 1;
        setResults([]);
      });
  }, [
    BrowseStore,
    state,
    searchState,
    pos,
    urlSearch,
    mapRange,
    isPositionReady,
  ]);

  const { run: debouncedFetch, loading: mapLoading } = useDebounceFn(
    () => {
      // 首次加载时立即执行，后续加载使用防抖
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        return fetchData();
      }
      return fetchData();
    },
    { wait: 300 },
  );

  // 当筛选条件、位置、路由变化时，从第1页重新加载
  useEffect(() => {
    const effectiveRegion = urlSearch.get("region");
    if (!effectiveRegion) return;
    // 移动端非地图模式下，不重复请求
    if (isMobile) {
      if (!exploreMapMode) return;
    }
    debouncedFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location,
    isMobile,
    exploreMapMode,
    mapRange,
    searchTrigger,
    isPositionReady,
  ]);

  return {
    mapResults: results,
    mapLoading,
  };
};

function ExplorePage() {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useMobile();
  const {
    exploreMapMode,
    pageCity,
    currentCity,
    mapRange,
    setMapRange,
    pos,
    cleanFilterAndSearch,
    onCityClick,
  } = useFilterContext();
  const [headBarHeight, setHeadBarHeight] = useState(0);
  const [mapSelectedRestaurants, setMapSelectedRestaurants] = useState([]);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const { BrowseStore } = useStores();
  const [swiperKey, setSwiperKey] = useState(0);
  const autoFitBoundsRef = useRef(true); // 同步追踪 autoFitBounds，避免 React state 异步更新问题
  const [showHomeIcon, setShowHomeIcon] = useState(false);
  const mapRef = useRef(null);
  const mobileListRef = useRef(null);
  const isMapChangedByUserRef = useRef(false);
  const isMapFullscreenRef = useRef(false);
  const mapReqIdRef = useRef(0);
  const pendingNearViewAfterMapUpdateRef = useRef(false);
  const pendingNearViewAfterReqIdRef = useRef(null);
  const pendingPopupLatLngRef = useRef(null); // 存储待设置 popup 的 latLng
  const lockRef = useRef(false); // 用于锁定地图操作，避免重复触发
  // 缓存上一次 tapMarkers 的 bounds 和计算出的 ids
  const lastTapCache = useRef({
    bounds: null,
    ids: [],
    idList: [],
    mapSelectedRestaurants: [],
  });
  const hasCalledNearViewRef = useRef(false);
  const [homeClickLoading, setHomeClickLoading] = useState(false);
  const prevPosRef = useRef(null);
  const prevMapResultsRef = useRef(null);
  const prevCityRef = useRef(null);
  const urlQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params;
  }, [location.search]);
  const avgPriceLabel = t("Browse.avgPrice");

  // 使用智能定位hook
  const isPositionReady = useSmartPosition();

  const { loading, loadingMore, results, total, isEmpty } = useResults({
    isMapFullscreen,
    isPositionReady,
  });
  const { mapResults, mapLoading } = useMapResults({
    mapReqIdRef,
    isPositionReady,
  });

  const fetchLoading = useMemo(() => {
    return loading || mapLoading || homeClickLoading || !isPositionReady;
  }, [loading, mapLoading, homeClickLoading, isPositionReady]);

  const handleMapHomeClick = () => {
    if (!currentCity) return mapRef.current?.backHome();
    if (currentCity?.key_word === urlQuery.get("region")) {
      mapRef.current?.backHome();
    } else {
      onCityClick(currentCity);
      // 记录一次“回到首页/回到当前城市”的意图：当下一次 mapResults 更新后，执行 nearView
      if (pos) {
        autoFitBoundsRef.current = false; // 同步设置，避免闭包读取到旧值
        setHomeClickLoading(true);
        pendingNearViewAfterMapUpdateRef.current = true;
        pendingNearViewAfterReqIdRef.current = mapReqIdRef.current;
      }
    }
  };

  /**
   * 地图展示数据
   * 移动端：使用 mapResults
   * 桌面端：使用 mapResults
   */
  const resultInMap = useMemo(() => {
    if (isMobile) {
      return exploreMapMode ? mapResults : [];
    } else {
      // 当清空了区域导致重新筛选，这时候是没用mapRange的
      return mapResults;
    }
  }, [mapResults, isMobile, exploreMapMode]);

  // 空状态提示
  const showEmpty = useMemo(() => {
    if (mapReqIdRef.current === 0) {
      return {
        visible: false,
        reason: null,
      };
    }

    // 有结果，不展示
    if (resultInMap.length > 0) {
      isMapChangedByUserRef.current = false;
      return {
        visible: false,
        reason: null,
      };
    }

    // 无结果，移动地图导致
    if (isMapChangedByUserRef.current) {
      isMapChangedByUserRef.current = false;
      return {
        visible: true,
        reason: "byMap",
        text: t("Collection.showAllRestaurants"),
      };
    }

    // 无结果，搜索导致
    isMapChangedByUserRef.current = false;
    return {
      visible: true,
      reason: "bySearch",
      text: t("Collection.showAllRestaurants"),
    };
  }, [resultInMap, pos, t]);

  const mapChangeHandler = ({ zoom, bounds }) => {
    // 用户手动操作地图时的回调
    const northWest = bounds.getNorthWest();
    const southEast = bounds.getSouthEast();

    // 当 northWest 与 southEast 相同时，bounds 退化为一个点，跳过更新
    if (northWest.lat === southEast.lat && northWest.lng === southEast.lng) {
      return;
    }

    autoFitBoundsRef.current = false; // 同步设置，避免闭包读取到旧值
    isMapChangedByUserRef.current = true; // 标记为用户手动操作
    lockRef.current = true; // 锁定地图操作，避免重复触发
    setMapRange({
      top_left_lat: northWest.lat,
      top_left_lng: northWest.lng,
      bottom_right_lat: southEast.lat,
      bottom_right_lng: southEast.lng,
    });
  };
  const tapMarkersHandler = (idList, bounds, latLng) => {
    // 移动端点击marker逻辑
    // 如果idList长度为1，表示点击了单个marker，需要筛选出bounds内的餐厅
    // 否则表示点击了聚合点，直接使用idList对应的餐厅
    if (isMobile) {
      const cache = lastTapCache.current;
      const boundsChanged = !cache.bounds || !cache.bounds.equals(bounds);

      // 如果idList为空，清空选择
      if (idList.length === 0) {
        return setMapSelectedRestaurants([]);
      }

      let ids = idList.slice();

      mapRef.current?.highlightMarker(ids[0]);

      // 当 bounds 未变化, 点击了视口内的其他单个marker，无需重新筛选，直接滑动过去
      if (!boundsChanged && ids.length === 1) {
        const targetIndex = cache.mapSelectedRestaurants.findIndex(
          (r) => r.id === idList[0],
        );
        setMapSelectedRestaurants(cache.mapSelectedRestaurants.slice());
        // 滑动到对应位置
        if (targetIndex >= -1 && mobileListRef.current) {
          setTimeout(() => {
            mobileListRef.current.slideTo(targetIndex);
          }, 100);
          return;
        }
      }

      // 仅点击了单个marker，筛选出bounds内其余49个的餐厅
      if (ids.length === 1) {
        ids = resultInMap
          .filter(({ lat_lon, id }) => {
            const lat = lat_lon.lat;
            const lng = lat_lon.lon;
            return bounds.contains([lat, lng]) && id !== idList[0];
          })
          .slice(0, 49) // 最多取50个
          .map((r) => r.id);

        ids.unshift(idList[0]); // 确保点击的marker在第一个
      }

      // 更新缓存
      cache.bounds = bounds;
      cache.ids = ids;
      cache.idList = idList.slice();

      if (ids.length) {
        lockRef.current = true; // 锁定地图操作，避免重复触发
        BrowseStore.getBrowseRestaurants({
          ...pos,
          extraParams: urlQuery.get("lang"),
          region: urlQuery.get("region"),
          "api-key": "cgecegcegcc",
          ids: ids.join(","),
          page: 1,
          per_page: idList.length === 1 ? 50 : idList.length,
        }).then((res) => {
          // 按 ids 顺序排序
          const idOrder = ids;
          const sorted = (res || [])
            .slice()
            .sort((a, b) => idOrder.indexOf(a.id) - idOrder.indexOf(b.id));
          setMapSelectedRestaurants(sorted);
          cache.mapSelectedRestaurants = sorted.slice();
          lockRef.current = false;
        });
      } else {
        setMapSelectedRestaurants([]);
        cache.mapSelectedRestaurants = [];
      }
      setSwiperKey((prev) => prev + 1);
    } else {
      // 桌面端点击marker逻辑
      if (idList.length) {
        // 先保存 latLng，等待 useEffect 中新数据渲染完成后再设置 popup
        pendingPopupLatLngRef.current = latLng || null;
        lockRef.current = true; // 锁定地图操作，避免重复触发
        BrowseStore.getBrowseRestaurants({
          ...pos,
          extraParams: urlQuery.get("lang"),
          region: urlQuery.get("region"),
          "api-key": "cgecegcegcc",
          ids: idList.join(","),
          page: 1,
          per_page: idList.length,
        }).then((res) => {
          setMapSelectedRestaurants(res);
          lockRef.current = false;
        });
      } else {
        pendingPopupLatLngRef.current = null;
        setMapSelectedRestaurants([]);
        lockRef.current = false;
      }
    }
  };
  const homeVisibleChangeHandler = (visible) => {
    setShowHomeIcon(!visible);
  };
  const handleSlideTo = useCallback((id) => {
    if (id) {
      mapRef.current?.highlightMarker(id);
    }
  }, []);
  const handleCleanFilter = () => {
    autoFitBoundsRef.current = true;
    cleanFilterAndSearch();
  };

  useEffect(() => {
    // 记录一次“回到首页/回到当前城市”的意图：当下一次 mapResults 更新后，执行 nearView
    if (pos && pageCity && currentCity && isMobile) {
      if(pageCity?.key_word === currentCity?.key_word) {
        autoFitBoundsRef.current = false; // 同步设置，避免闭包读取到旧值
        pendingNearViewAfterMapUpdateRef.current = true;
        pendingNearViewAfterReqIdRef.current = mapReqIdRef.current;
      }
    }
  }, [pageCity, pos, currentCity, isMobile]);

  useEffect(() => {
    const getHeadBarElement = () => document.querySelector(".ui-head-bar");

    let rafId = null;
    const measure = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const headBarElement = getHeadBarElement();
        if (!headBarElement) {
          setHeadBarHeight(30);
          return;
        }
        const next =
          Math.ceil(headBarElement.getBoundingClientRect().height) -
          (window.innerWidth <= 768 ? 130 : 0);
        setHeadBarHeight((prev) => (prev === next ? prev : next));
      });
    };

    measure();

    const headBarElement = getHeadBarElement();
    const resizeObserver =
      headBarElement && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;

    if (resizeObserver && headBarElement)
      resizeObserver.observe(headBarElement);
    window.addEventListener("resize", measure, { passive: true });

    return () => {
      window.removeEventListener("resize", measure);
      if (resizeObserver) resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // 当切换地图模式, 重置mapReqIdRef
  useEffect(() => {
    mapReqIdRef.current = 0;
    hasCalledNearViewRef.current = false;
  }, [exploreMapMode]);

  // 当mapRange被清空时，重置autoFitBoundsRef
  useEffect(() => {
    autoFitBoundsRef.current = !mapRange;
  }, [mapRange]);

  // 处理全屏模式下的body滚动
  useEffect(() => {
    isMapFullscreenRef.current = isMapFullscreen;
    if (isMapFullscreen) {
      // 禁用body滚动
      document.body.style.overflow = "hidden";
    } else {
      // 恢复body滚动
      document.body.style.overflow = "auto";
    }
    return () => {
      // 清理：组件卸载时恢复body滚动
      document.body.style.overflow = "auto";
    };
  }, [isMapFullscreen]);

  useEffect(() => {
    setMapSelectedRestaurants([]);
    pendingPopupLatLngRef.current = null;
    mapRef.current?.closePopup();
  }, [mapResults]);

  // 当首次获取到mapResults后调用nearView，pos变化导致的mapResults变化也视为首次

  useEffect(() => {
    lockRef.current = false;
    if (!mapResults || mapResults.length === 0) return;
    if (!mapRef.current?.nearView || !pos) return;

    const isPosChanged =
      prevPosRef.current?.lat !== pos?.lat ||
      prevPosRef.current?.lng !== pos?.lng ||
      prevCityRef.current?.key_word !== currentCity?.key_word;
    const isNewMapResults = prevMapResultsRef.current !== mapResults;

    const shouldForceNearViewAfterHomeClick =
      pendingNearViewAfterMapUpdateRef.current &&
      pendingNearViewAfterReqIdRef.current != null &&
      mapReqIdRef.current > pendingNearViewAfterReqIdRef.current;

    // pos变化时，重置标记，使下次mapResults更新时重新调用nearView
    if (isPosChanged) {
      hasCalledNearViewRef.current = false;
    }

    // 仅在mapResults实际更新且尚未调用过nearView时触发
    if (
      isNewMapResults &&
      (shouldForceNearViewAfterHomeClick || (isMobile ? !hasCalledNearViewRef.current : false))
    ) {
      if (currentCity?.key_word === urlQuery.get("region")) {
        mapRef.current.backHome(shouldForceNearViewAfterHomeClick);
      }
      hasCalledNearViewRef.current = true;

      if (shouldForceNearViewAfterHomeClick) {
        pendingNearViewAfterMapUpdateRef.current = false;
        pendingNearViewAfterReqIdRef.current = null;
        setTimeout(() => {
          setHomeClickLoading(false);
        }, 1000);
      }
    }

    prevPosRef.current = pos;
    prevMapResultsRef.current = mapResults;
    prevCityRef.current = currentCity;
  }, [mapResults, pos, currentCity, isMobile]);

  // 桌面端切换全屏状态的回调（不触发请求）
  const handleFullscreenChange = () => {
    setIsMapFullscreen((prev) => !prev);
    window.dispatchEvent(new Event("resize"));
  };

  return (
    <>
      <div
        className={cn("expore-page", {
          "expore-page-fullscreen": isMapFullscreen,
        })}
        style={{ "--explore-headbar-height": `${headBarHeight}px` }}
      >
        {(isMobile ? !exploreMapMode : !isMapFullscreen) && (
          <div className="expore-page-list">
            {!isMobile && (
              <Affix offsetTop={headBarHeight}>
                <Filters />
              </Affix>
            )}
            <RestaurantList
              loading={fetchLoading}
              loadingMore={loadingMore}
              results={results}
              total={total}
              isEmpty={isEmpty}
              mapRef={mapRef}
            />
          </div>
        )}

        {(isMobile ? exploreMapMode : true) && (
          <div className="expore-page-map">
            <MapView
              ref={mapRef}
              lockRef={lockRef}
              restaurants={resultInMap}
              pageCity={pageCity}
              onMapChange={mapChangeHandler}
              tapMarkers={tapMarkersHandler}
              onHomeVisibleChange={homeVisibleChangeHandler}
              autoFitBoundsRef={autoFitBoundsRef}
              pos={pos}
              mapRange={mapRange}
            />
            {isMobile ? (
              <MapViewMobileTool
                showEmpty={showEmpty}
                handleCleanFilter={handleCleanFilter}
                showHomeIcon={showHomeIcon}
                handleMapHomeClick={handleMapHomeClick}
                mobileListRef={mobileListRef}
                swiperKey={swiperKey}
                mapSelectedRestaurants={mapSelectedRestaurants}
                avgPriceLabel={avgPriceLabel}
                handleSlideTo={handleSlideTo}
                loading={fetchLoading}
              />
            ) : (
              <MapViewDesktopTool
                mapRef={mapRef}
                pendingPopupLatLngRef={pendingPopupLatLngRef}
                showEmpty={showEmpty}
                handleCleanFilter={handleCleanFilter}
                showHomeIcon={showHomeIcon}
                handleMapHomeClick={handleMapHomeClick}
                mapSelectedRestaurants={mapSelectedRestaurants}
                avgPriceLabel={avgPriceLabel}
                loading={fetchLoading}
                isFullscreen={isMapFullscreen}
                handleToggleFullscreen={handleFullscreenChange}
                handleZoomIn={() => mapRef.current?.zoomIn()}
                handleZoomOut={() => mapRef.current?.zoomOut()}
              />
            )}
          </div>
        )}
      </div>
      {(isMobile ? !exploreMapMode : true) && <Footer isFixed={true} />}
    </>
  );
}

export default observer(ExplorePage);
